/**
 * Accessible CAD money draft → integer cents parser for public valuation intake.
 * Authoritative conversion uses integer/string arithmetic only (no float multiply).
 *
 * Presentation helpers below support familiar Rentalyzer focus/blur formatting for
 * bilingual public pages. FormattedNumberInput is not reused directly: it always
 * formats with fr-CA, coerces via Number(), and uses 0 decimal places for currency,
 * which is unsafe for public FR/EN cents drafts.
 */

export const PUBLIC_CAD_MONEY_MAX_CENTS = 1_000_000_000; // CAD $10,000,000

/**
 * Strip currency decoration and locale thousands separators from pasted/typed text
 * so parseCadDraftToCents can run on an unambiguous draft. Does not convert to cents.
 *
 * @param {unknown} raw
 * @param {'fr'|'en'} [language='fr']
 * @returns {string}
 */
export function normalizeCadMoneyPresentationDraft(raw, language = 'fr') {
  if (raw == null) return '';
  if (typeof raw !== 'string' && typeof raw !== 'number') return '';

  let text = String(raw).trim();
  if (!text) return '';

  // Presentation-only noise: currency code / symbol (keep digits and separators).
  text = text.replace(/CAD/gi, '').replace(/\$/g, '').trim();
  if (!text) return '';

  // Ordinary spaces / narrow no-break spaces are thousands separators.
  text = text.replace(/[\s\u00a0\u202f]/g, '');
  if (!text) return '';

  const hasComma = text.includes(',');
  const hasPeriod = text.includes('.');

  if (language === 'en') {
    if (hasComma && hasPeriod) {
      // EN currency paste: 1,234.56
      text = text.replace(/,/g, '');
    } else if (hasComma && !hasPeriod && /^\d{1,3}(,\d{3})+$/.test(text)) {
      // EN whole dollars with thousands: 1,234
      text = text.replace(/,/g, '');
    }
  } else if (hasComma && hasPeriod) {
    // FR mixed: 1.234,56
    text = text.replace(/\./g, '');
  } else if (hasPeriod && !hasComma && /^\d{1,3}(\.\d{3})+$/.test(text)) {
    // FR whole dollars with dot thousands: 1.234
    text = text.replace(/\./g, '');
  }

  return text;
}

/**
 * @param {unknown} raw
 * @returns {{ ok: true, cents: number } | { ok: false, reason: string }}
 */
export function parseCadDraftToCents(raw) {
  if (raw == null) return { ok: false, reason: 'empty' };
  if (typeof raw !== 'string' && typeof raw !== 'number') return { ok: false, reason: 'invalid' };

  let text = String(raw).trim();
  if (!text) return { ok: false, reason: 'empty' };
  if (/[eE]/.test(text)) return { ok: false, reason: 'invalid' };
  if (/^[-−]/.test(text) || text.includes('-')) return { ok: false, reason: 'negative' };

  // Allow currency decoration on authoritative parse so pasted "12 000 $" works.
  text = text.replace(/CAD/gi, '').replace(/\$/g, '').trim();
  if (!text) return { ok: false, reason: 'empty' };

  // Allow ordinary spaces / narrow no-break spaces as thousands separators only.
  const compact = text.replace(/[\s\u00a0\u202f]/g, '');
  if (!compact) return { ok: false, reason: 'empty' };
  if (!/^[0-9.,]+$/.test(compact)) return { ok: false, reason: 'invalid' };

  const commaCount = (compact.match(/,/g) || []).length;
  const periodCount = (compact.match(/\./g) || []).length;
  if (commaCount + periodCount > 1) return { ok: false, reason: 'ambiguous' };

  let whole;
  let fraction = '';
  if (commaCount === 1) {
    const parts = compact.split(',');
    whole = parts[0];
    fraction = parts[1] ?? '';
  } else if (periodCount === 1) {
    const parts = compact.split('.');
    whole = parts[0];
    fraction = parts[1] ?? '';
  } else {
    whole = compact;
  }

  if (!whole || !/^\d+$/.test(whole)) return { ok: false, reason: 'invalid' };
  if (fraction.length > 2) return { ok: false, reason: 'decimals' };
  if (fraction && !/^\d{1,2}$/.test(fraction)) return { ok: false, reason: 'invalid' };

  // Integer cents: dollars*100 + padded fraction digits (no Number() multiply of dollars).
  const dollars = BigInt(whole);
  const fracPadded = `${fraction}00`.slice(0, 2);
  const cents = dollars * 100n + BigInt(fracPadded);
  if (cents < 0n) return { ok: false, reason: 'negative' };
  if (cents > BigInt(PUBLIC_CAD_MONEY_MAX_CENTS)) return { ok: false, reason: 'upperBound' };
  return { ok: true, cents: Number(cents) };
}

/**
 * Parse after locale presentation normalization (paste of formatted EN/FR amounts).
 * @param {unknown} raw
 * @param {'fr'|'en'} [language='fr']
 */
export function parseCadMoneyInputToCents(raw, language = 'fr') {
  return parseCadDraftToCents(normalizeCadMoneyPresentationDraft(raw, language));
}

/**
 * Editable draft shown on focus — no thousands separators, locale decimal separator.
 * Built from integer cents only (no float multiply).
 *
 * @param {number} cents
 * @param {'fr'|'en'} [language='fr']
 * @returns {string}
 */
export function cadCentsToEditDraft(cents, language = 'fr') {
  if (!Number.isInteger(cents) || cents < 0) return '';
  const whole = Math.trunc(cents / 100);
  const fraction = cents % 100;
  if (fraction === 0) return String(whole);
  const sep = language === 'en' ? '.' : ',';
  const fracStr = String(fraction).padStart(2, '0').replace(/0+$/, '');
  return `${whole}${sep}${fracStr}`;
}

/**
 * Blur/display formatting for public money inputs (Intl CAD, NBSP normalized to space).
 * Display-only — never used as storage.
 *
 * @param {number} cents
 * @param {'fr'|'en'} [language='fr']
 * @returns {string}
 */
export function formatCadCentsForMoneyInput(cents, language = 'fr') {
  const formatted = formatCadCentsForDisplay(cents, language);
  if (formatted == null) return '';
  return formatted.replace(/\u00a0|\u202f/g, ' ');
}

/**
 * Format a draft for blur display when it parses; otherwise return the normalized draft.
 *
 * @param {unknown} raw
 * @param {'fr'|'en'} [language='fr']
 * @returns {{ display: string, draft: string, parsed: ReturnType<typeof parseCadDraftToCents> }}
 */
export function formatCadDraftForMoneyInput(raw, language = 'fr') {
  const draft = normalizeCadMoneyPresentationDraft(raw, language);
  const parsed = parseCadDraftToCents(draft);
  if (!parsed.ok) {
    return { display: draft, draft, parsed };
  }
  return {
    display: formatCadCentsForMoneyInput(parsed.cents, language),
    draft: cadCentsToEditDraft(parsed.cents, language),
    parsed,
  };
}

/** Display-only CAD formatting from integer cents (admin/UI). Never used as storage. */
export function formatCadCentsForDisplay(cents, language = 'fr') {
  if (cents == null) return null;
  if (!Number.isInteger(cents)) return null;
  const locale = language === 'en' ? 'en-CA' : 'fr-CA';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}
